<template>
    <div>
        <!-- Page Title and Breadcrumn-->
        <Breadcrumb
            :title="$tc('TXT_PRODUCT', 2)"
            :items="breadcrumbItems"
            :home-label="$t('LBL_HOME')"
            :home-url="route('landing')"
        />
        <!-- breadcrumb end -->

        <div
            class="w-full bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-overflow"
        >
            <!-- Search & Action Bar -->
            <div class="flex flex-wrap items-center justify-between gap-4 p-4 sm:flex-nowrap">
                <!-- Search Box -->
                <div class="relative w-full sm:w-auto">
                    <label
                        for="table-search"
                        class="sr-only"
                        >Search</label
                    >
                    <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <svg
                            class="w-5 h-5 text-gray-500 dark:text-gray-400"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                clip-rule="evenodd"
                            />
                        </svg>
                    </div>
                    <t-input
                        id="retailer-search-box"
                        v-model="search"
                        type="text"
                        variant="success"
                        class="w-full sm:w-80 pl-10 pr-4 py-2.5 text-sm rounded-lg border border-gray-300 focus:ring-2 focus:ring-green-500 focus:outline-none dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                        placeholder="Search for product"
                        @keypress.enter="fetchFilteredProducts(products.current_page)"
                    />
                </div>

                <!-- Action Buttons -->
                <div class="flex flex-wrap gap-2">
                    <t-rich-select
                        v-model="selectedCategory"
                        class="w-60 text-sm"
                        :close-on-select="false"
                        :options="categories"
                        :clearable="true"
                        value-attribute="id"
                        text-attribute="name"
                        placeholder="Filter by category"
                    />

                    <t-rich-select
                        v-model="selectedBrand"
                        class="w-60 text-sm"
                        :clearable="true"
                        :close-on-select="false"
                        :options="brands"
                        value-attribute="id"
                        text-attribute="name"
                        placeholder="Filter by brand"
                    />
                    <t-button
                        v-if="(isWholesaler && kycVerified) || isAdmin"
                        class="inline-flex items-center rounded-md"
                        variant="success"
                        @click="$emit('toggleRightSidebar')"
                    >
                        <span>Add Product</span>
                    </t-button>
                </div>
            </div>

            <div
                class="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-none shadow-md"
            >
                <div class="overflow-x-auto">
                    <!-- Table content to list the Products -->
                    <table
                        class="min-w-[700px] w-full table-auto text-sm sm:text-base text-left text-gray-700 dark:text-gray-300"
                    >
                        <thead class="bg-gray-100 dark:bg-gray-700 text-sm font-semibold">
                            <tr>
                                <th class="px-4 py-3">#</th>
                                <th class="px-4 py-3">IMAGE</th>
                                <th class="px-4 py-3">NAME</th>
                                <th class="px-4 py-3">BRAND</th>
                                <th class="px-4 py-3">CATEGORY</th>
                                <th class="px-4 py-3">PRICE</th>
                                <th class="px-4 py-3">STOCK</th>
                                <th class="px-4 py-3">ACTIONS</th>
                            </tr>
                        </thead>
                        <tbody>
                            <template v-for="(row, rowIndex) in products.data">
                                <tr
                                    class="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition duration-200"
                                >
                                    <td class="px-4 py-3">{{ rowIndex + 1 + (currentPage - 1) * perPage }}</td>
                                    <td class="px-4 py-3">
                                        <div
                                            v-if="row.images.length"
                                            class="flex h-10 gap-2"
                                        >
                                            <img
                                                class="object-cover h-full"
                                                :src="row.images[0].image_path"
                                                :alt="row.name"
                                            />
                                        </div>
                                        <div
                                            v-else
                                            class="h-10"
                                        >
                                            <img
                                                :src="defaultImage"
                                                class="object-contain h-full"
                                                :alt="row.name"
                                            />
                                        </div>
                                    </td>
                                    <td class="px-4 my-5 line-clamp-1">{{ row.name }}</td>
                                    <td class="px-4 py-3">{{ row.brand?.name || '--' }}</td>
                                    <td class="px-4 py-3">{{ row.category_name }}</td>
                                    <td class="px-4 py-3 text-green-500">
                                        {{ config(common.currency, row.owner) }} {{ parseFloat(row.price).toFixed(2) }}
                                    </td>
                                    <td class="px-4 py-3">{{ row.available_stock }}</td>
                                    <td class="px-4 py-3 relative">
                                        <div class="inline-flex shadow-sm rounded-md overflow-visible">
                                            <!-- Primary Action -->
                                            <div>
                                                <t-button
                                                    variant="group"
                                                    size="sm"
                                                    class="rounded-none rounded-l-md"
                                                    @click="
                                                        newTab('products.show', {
                                                            dummy: `${convertToKebabCase(
                                                                row.brand.name,
                                                            )}-${convertToKebabCase(row.name)}`,
                                                            product: row.slug,
                                                        })
                                                    "
                                                >
                                                    <span>Show</span>
                                                </t-button>
                                            </div>

                                            <!-- Dropdown Toggle Button -->
                                            <!-- <div
                                                class="relative dropdown-wrapper"
                                                :ref="'dropdown-container-' + row.id"
                                            >
                                                <t-button
                                                    variant="groupoutline"
                                                    size="sm"
                                                    class="border rounded-none rounded-r-md py-2 w-10"
                                                    @click.stop="toggleDropdown(row.id)"
                                                >
                                                    . . .
                                                </t-button> -->

                                            <!-- Dropdown Trigger -->
                                            <t-button
                                                variant="groupoutline"
                                                size="sm"
                                                class="border rounded-r-md py-2 w-10"
                                                @click.stop="toggleDropdown(row.id, $event)"
                                            >
                                                . . .
                                            </t-button>

                                            <!-- Dropdown Menu -->
                                            <!-- Portal for Dropdown -->
                                            <portal
                                                to="dropdown-target"
                                                v-if="activeDropdownId === row.id"
                                            >
                                                <div
                                                    class="absolute right-0 mt-2 w-44 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50"
                                                    :style="dropdownPosition"
                                                >
                                                    <ul class="text-sm text-gray-700 dark:text-gray-200">
                                                        <li>
                                                            <t-button
                                                                v-if="(isWholesaler && kycVerified) || isAdmin"
                                                                variant="outline"
                                                                class="block w-full px-4 py-2 border-none hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                                                                @click="showDescriptionModal(row.id)"
                                                            >
                                                                <span>Description</span>
                                                            </t-button>
                                                        </li>
                                                        <li>
                                                            <t-button
                                                                variant="outline"
                                                                class="block w-full px-4 py-2 border-none hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                                                                @click="editProduct(row.id)"
                                                            >
                                                                <span>Edit</span>
                                                            </t-button>
                                                        </li>
                                                        <li>
                                                            <t-button
                                                                variant="outline"
                                                                class="block w-full px-4 py-2 border-none hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                                                                @click="deleteProduct(row.id)"
                                                            >
                                                                <span>Delete</span>
                                                            </t-button>
                                                        </li>
                                                        <li>
                                                            <t-button
                                                                v-if="(isWholesaler && kycVerified) || isAdmin"
                                                                variant="outline"
                                                                class="block w-full px-4 py-2 border-none hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                                                                @click="showUpdateStockModal(row.id)"
                                                            >
                                                                <span>Stock</span>
                                                            </t-button>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </portal>
                                            <!-- </div> -->
                                        </div>
                                    </td>
                                </tr>
                            </template>
                            <tr
                                v-if="!products.data.length"
                                class="text-center"
                            >
                                <td
                                    colspan="8"
                                    class="px-4 py-4 text-gray-500 dark:text-gray-400"
                                >
                                    {{ $t('LBL_DATA_FOUND') }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <div
                    v-if="products.data.length"
                    class="flex justify-center items-center bg-gray-100 dark:bg-gray-900 px-4 py-4 border-t dark:border-gray-700"
                >
                    <t-pagination
                        v-model="currentPage"
                        variant="rounded"
                        :total-items="totalRows"
                        :per-page="perPage"
                        :limit="limit"
                        :disabled="!products.data.length"
                        @change="fetchFilteredProducts"
                    />
                </div>
                <p class="sm:hidden text-xs text-center text-gray-500 py-2">↔ Swipe left/right to scroll the table</p>
            </div>
        </div>

        <t-modal
            ref="descriptionModal"
            v-model="viewDescriptionModal"
            header="Product Description"
        >
            <div v-html="currentProduct?.details" />
        </t-modal>

        <t-modal
            ref="stockModal"
            v-model="updateStockModal"
            header="Adjust Product Stock"
            size="md"
        >
            <div class="flex flex-col gap-6 p-2 text-gray-800 dark:text-white">
                <!-- Adjustment Type -->
                <div>
                    <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                        Adjustment Type*
                    </label>
                    <div class="flex items-center gap-6">
                        <label class="inline-flex items-center">
                            <t-radio
                                v-model="stock.changeType"
                                name="change_type"
                                :value="1"
                            />
                            <span class="ml-2 text-sm">Increase</span>
                        </label>
                        <label class="inline-flex items-center">
                            <t-radio
                                v-model="stock.changeType"
                                name="change_type"
                                :value="0"
                            />
                            <span class="ml-2 text-sm">Decrease</span>
                        </label>
                    </div>
                </div>

                <!-- Quantity Input -->
                <div>
                    <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"> Quantity* </label>
                    <t-input
                        v-model="stock.changeAmount"
                        placeholder="Enter quantity to adjust"
                        type="number"
                        min="1"
                        class="w-full"
                    />
                </div>

                <!-- Remarks -->
                <div>
                    <label class="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"> Remarks* </label>
                    <t-textarea
                        v-model="stock.remarks"
                        placeholder="Reason for adjustment"
                        rows="3"
                        class="w-full"
                    />
                </div>
            </div>

            <!-- Footer Buttons -->
            <template #footer>
                <div class="flex justify-end gap-3 px-4 py-2">
                    <t-button
                        type="button"
                        variant="outline px-4 py-2"
                        @click="$refs.stockModal.hide()"
                    >
                        Cancel
                    </t-button>
                    <t-button
                        type="button"
                        variant="success"
                        :disabled="!stock.changeAmount || !stock.remarks"
                        @click="updateStock"
                    >
                        Confirm
                    </t-button>
                </div>
            </template>
        </t-modal>
        <portal-target
            name="dropdown-target"
            multiple
        ></portal-target>
    </div>
</template>

<script>
    import _ from 'lodash';
    import common from '@/common';
    import Breadcrumb from '../../Components/Breadcrumb.vue';

    export default {
        name: 'ProductsAdminView',

        components: {
            Breadcrumb,
        },

        props: {
            products: {
                type: Object,
                required: true,
            },
            wholesalers: {
                type: Array,
                required: true,
            },
            categories: {
                type: Array,
                required: true,
            },
            brands: {
                type: Array,
                required: true,
            },
            appliedFilters: {
                type: Object,
                required: true,
            },
            sortBy: {
                validator: (prop) => typeof prop === 'string' || prop === null,
                required: true,
            },
        },

        data() {
            return {
                common,
                breadcrumbItems: [{ label: this.$tc('TXT_PRODUCT', 1) }],
                // :items="[{ label: $tc('TXT_PRODUCT', 1) }]",
                opened: [],
                mounted: false,
                currentPage: null,
                limit: 5,
                perPage: this.products.per_page,
                totalRows: this.products.total,
                currentProduct: null,
                updateStockModal: false,
                viewDescriptionModal: false,
                stock: {
                    changeType: 1,
                    changeAmount: null,
                    remarks: null,
                },

                selectedCategory: null,
                selectedBrand: null,
                search: null,
                activeDropdownId: null,
                dropdownPosition: {
                    top: '0px',
                    right: '0px',
                },
            };
        },

        computed: {
            disabled() {
                return !this.products.data.length;
            },

            defaultImage() {
                return `${process.env.MIX_SENTRY_DSN_PUBLIC}/storage/default/default.png`;
            },
        },

        watch: {
            search() {
                if (this.mounted) {
                    this.debouncedFetchFilteredProducts(this.products.current_page);
                }
            },

            selectedCategory() {
                if (this.mounted) {
                    this.fetchFilteredProducts(this.products.current_page);
                }
            },

            selectedBrand() {
                if (this.mounted) {
                    this.fetchFilteredProducts(this.products.current_page);
                }
            },
        },

        created() {
            this.currentPage = this.products.current_page;
        },

        mounted() {
            if (this.appliedFilters.categoryIds && this.appliedFilters.categoryIds.length) {
                this.selectedCategory = parseInt(this.appliedFilters.categoryIds[0]);
            }
            if (this.appliedFilters.brandIds && this.appliedFilters.brandIds.length) {
                this.selectedBrand = parseInt(this.appliedFilters.brandIds[0]);
            }
            if (this.appliedFilters.keyword) {
                this.search = this.appliedFilters.keyword;
            }

            // ✅ Close dropdown on outside click
            document.addEventListener('click', this.handleClickOutside);

            this.debouncedFetchFilteredProducts = _.debounce(this.fetchFilteredProducts, 2000);

            this.$nextTick(() => {
                this.mounted = true;
            });
        },

        methods: {
            // toggleRow(id) {
            //     const index = this.opened.indexOf(id);
            //     if (index > -1) {
            //         this.opened.splice(index, 1);
            //     } else {
            //         this.opened.push(id);
            //     }
            // },
            toggleDropdown(userId, event) {
                if (this.activeDropdownId === userId) {
                    this.activeDropdownId = null;
                    return;
                }

                const trigger = event.currentTarget.getBoundingClientRect();
                const scrollY = window.scrollY || window.pageYOffset;
                const scrollX = window.scrollX || window.pageXOffset;

                // Assume dropdown width is 176px (w-44) = 11rem
                const dropdownWidth = 176;
                this.dropdownPosition = {
                    top: `${trigger.bottom + scrollY}px`,
                    left: `${trigger.right + scrollX - dropdownWidth}px`,
                };

                this.activeDropdownId = userId;
            },

            handleClickOutside(event) {
                // Close dropdown if the click is outside any open dropdown
                if (!event.target.closest('.dropdown-wrapper')) {
                    this.activeDropdownId = null;
                }
            },

            editProduct(productId) {
                const product = this.products.data.find((item) => item.id === productId);
                this.$emit('edit', product);
            },

            showUpdateStockModal(productId) {
                this.updateStockModal = true;
                this.currentProduct = this.products.data.find((product) => product.id === productId);
            },

            hideUpdateStockModal() {
                this.updateStockModal = false;
                this.currentProduct = null;
                this.stock = {
                    changeType: 1,
                    changeAmount: null,
                };
            },

            updateStock() {
                if (this.currentProduct) {
                    this.$dialog
                        .confirm({
                            title: 'Confirm',
                            text: 'Are you sure you change your product stock?',
                        })
                        .then((result) => {
                            if (result.isOk) {
                                this.$inertia
                                    .post(route('stock.update', this.currentProduct.slug), {
                                        change_type: !!this.stock.changeType,
                                        change_amount: this.stock.changeAmount,
                                        remarks: this.stock.remarks,
                                    })
                                    .then(() => {
                                        this.hideUpdateStockModal();
                                    });
                            }
                        });
                }
            },

            showDescriptionModal(productId) {
                this.viewDescriptionModal = true;
                this.currentProduct = this.products.data.find((product) => product.id === productId);
            },

            deleteProduct(productId) {
                const product = this.products.data.find((item) => item.id === productId);
                this.$dialog
                    .confirm({
                        title: 'Confirm',
                        text: `Are you sure you want to delete ${product.name}?`,
                    })
                    .then((result) => {
                        if (result.isOk) {
                            this.$emit('delete', product);
                        }
                    });
            },

            fetchFilteredProducts(newPage) {
                if (this.$page.url.includes('/search/')) {
                    this.$inertia.get(
                        route('search.show', {
                            // minPrice: this.$refs.filters.priceRange[0],
                            // maxPrice: this.$refs.filters.priceRange[1],
                            // wholesalerIds: this.appliedFilters.wholesalerIds,
                            // sortBy: this.sortBy,
                            keyword: this.search,
                            categoryIds: [this.selectedCategory],
                            brandIds: [this.selectedBrand],
                            page: newPage,
                        }),
                    );
                } else {
                    this.$inertia.get(
                        route('products.index', {
                            // minPrice: this.$refs.filters.priceRange[0],
                            // maxPrice: this.$refs.filters.priceRange[1],
                            // wholesalerIds: this.appliedFilters.wholesalerIds,
                            // sortBy: this.sortBy,
                            keyword: this.search,
                            categoryIds: [this.selectedCategory],
                            brandIds: [this.selectedBrand],
                            page: newPage,
                        }),
                    );
                }
            },
        },

        beforeDestroy() {
            document.removeEventListener('click', this.handleClickOutside);
        },
    };
</script>
